import { useCallback } from 'react';
import { Joystick, JoystickShape } from 'react-joystick-component';
import { IJoystickUpdateEvent } from 'react-joystick-component/build/lib/Joystick';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/material';
import { KeyboardCmd } from '@/shared/components';
import { assetsBaseUrl, KeyboardShortcut, KeyboardShortcuts } from '@/shared/constants';
import SpringSlider from './SpringSlider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Tracking from '@/shared/services/tracking';
const JOYSTICK_TOP = `${assetsBaseUrl}/images/joystick-top.svg`;

type Props = {
  disabled: boolean;
  onChange: (pan: number, tilt: number) => unknown;
  onZoomChange?: (zoomDiff: number) => unknown;
  /** Only the zoom slider (target mode replaces the rest). */
  zoomOnly?: boolean;
};

const MAX_VELOCITY = 3; // degrees/frame
const SIZE = 130;
const MAX_DRAG_DISTANCE = SIZE * 2;
const THROTTLE = (1 / 60) * 1000; // 60fps in milliseconds

const toVelocity = (dragDistance: number) => (dragDistance / MAX_DRAG_DISTANCE) * MAX_VELOCITY;

// Normalize slider value (-100 to +100) to zoom direction (-1 to +1)
// Slider UP (+100) → zoom IN → negative zoomDiff (matches keyboard R key)
// Slider DOWN (-100) → zoom OUT → positive zoomDiff (matches keyboard F key)
const sliderToZoomDiff = (sliderValue: number) => -sliderValue / 100;

// Tooltip content with styled keyboard key
const PTZShortcut = ({ shortcut }: { shortcut: KeyboardShortcut }) => (
  <Stack direction="row" spacing={1} sx={{ m: 0.5, alignItems: 'center' }}>
    <Typography sx={{ fontSize: '0.75rem' }}>{shortcut.command}</Typography>
    <KeyboardCmd keys={shortcut.keys} />
  </Stack>
);

const PtzJoystick = ({ disabled, onChange, onZoomChange, zoomOnly }: Props) => {
  const { zoomToggle } = useFlags();

  const handleZoomChange = useCallback(
    (value: number) => {
      // Convert slider position (-100 to +100) to normalized direction (-1 to +1)
      // The further from center, the faster the zoom
      const zoomDiff = sliderToZoomDiff(value);
      onZoomChange?.(zoomDiff);
      Tracking.getInstance().track('PTZ Control', { category: 'Clips', action: zoomDiff > 0 ? 'Zoom In' : 'Zoom Out' });
    },
    [onZoomChange],
  );

  const handleMove = (e: IJoystickUpdateEvent) => {
    const pan = toVelocity(e.x ?? 0);
    const tilt = toVelocity(e.y ?? 0);
    onChange(pan, tilt);
    Tracking.getInstance().track('PTZ Control', { category: 'Clips', action: `Pan: ${pan.toFixed(2)}, Tilt: ${tilt.toFixed(2)}` });
  };

  const handleStop = (_e: IJoystickUpdateEvent) => {
    // noop
    onChange(0, 0);
  };
  const M_Y = 0.5;

  const zoomSlider = zoomToggle && (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <PTZShortcut shortcut={KeyboardShortcuts.ZoomIn} />
      <SpringSlider aria-label="Zoom" disabled={disabled} onChange={handleZoomChange} />
      <PTZShortcut shortcut={KeyboardShortcuts.ZoomOut} />
    </Box>
  );

  if (zoomOnly) return zoomSlider || null;

  return (
    <Stack direction="row" spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: `1fr ${SIZE}px 1fr`,
          gridTemplateColumns: `1fr ${SIZE}px 1fr`,
          justifyItems: 'center',
          alignItems: 'center',
        }}
      >
        <Box sx={{ gridColumnStart: 2, gridRowStart: 1 }}>
          <PTZShortcut shortcut={KeyboardShortcuts.TiltUp} />
        </Box>

        <Box sx={{ gridColumnStart: 3, gridRowStart: 2 }}>
          <PTZShortcut shortcut={KeyboardShortcuts.PanRight} />
        </Box>

        <Box sx={{ gridColumnStart: 2, gridRowStart: 3 }}>
          <PTZShortcut shortcut={KeyboardShortcuts.TiltDown} />
        </Box>

        <Box sx={{ gridRowStart: 2 }}>
          <PTZShortcut shortcut={KeyboardShortcuts.PanLeft} />
        </Box>

        <Box
          sx={{
            boxShadow: 20,
            display: 'inline-block',
            borderRadius: '20px',
            overflow: 'hidden',
            gridColumnStart: 2,
            gridRowStart: 2,
          }}
        >
          <Joystick
            baseColor="#27272E"
            baseShape={JoystickShape.Square}
            disabled={disabled}
            move={handleMove}
            size={SIZE}
            stickImage={JOYSTICK_TOP}
            stop={handleStop}
            throttle={THROTTLE}
          />
        </Box>
      </Box>
      {zoomSlider}
    </Stack>
  );
};

export default PtzJoystick;
